import React, { useEffect, useState } from 'react'
import { HiOutlineMegaphone } from 'react-icons/hi2'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { toast } from 'sonner'
import { apiClient } from '../../common/apiClient'
import { memoTitle } from '../../common/memoReader'
import { useAuth } from '../../contexts/AuthContext'
import type { PendingMemo } from '../../types/app'
import { DocumentReader } from '../reader/DocumentReader'
import { fetchPdfDocument, usePdfPages } from '../reader/usePdfPages'

interface MemoReadModalProps {
  memo: PendingMemo
  /** Whose memo list this came from. */
  email: string
  open: boolean
  onClose: () => void
  onAccepted: (junctionId: string) => void
}

export const MemoReadModal: React.FC<MemoReadModalProps> = ({
  memo,
  email,
  open,
  onClose,
  onAccepted,
}) => {
  const { getAccessToken } = useAuth()
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const pages = usePdfPages(pdfDoc)
  const title = memoTitle(memo)

  useEffect(() => {
    const load = async () => {
      try {
        const path = `/api/salesforce/memos/${encodeURIComponent(memo.memoId)}/pdf?email=${encodeURIComponent(email)}`
        setPdfDoc(await fetchPdfDocument(path, await getAccessToken()))
      } catch (err) {
        console.error('Error loading memo:', err)
        setFailed(true)
        toast.error('Failed to load memo')
      } finally {
        setIsLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memo.memoId, email])

  const handleConfirm = async () => {
    try {
      await apiClient.post('/api/salesforce/memos/accept', { junctionId: memo.junctionId, email })
      onAccepted(memo.junctionId)
    } catch {
      toast.error('Failed to accept memo. Please try again.')
    }
  }

  return (
    <DocumentReader
      open={open}
      onClose={onClose}
      noun="memo"
      icon={<HiOutlineMegaphone className="h-5 w-5" />}
      eyebrow={<span>{memo.fromName ? `From ${memo.fromName}` : 'Company memo'}</span>}
      title={title}
      pages={pages}
      pageCount={pdfDoc?.numPages}
      isLoading={isLoading}
      failed={failed}
      canConfirm
      onConfirm={handleConfirm}
    />
  )
}
